import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useMutation } from "@tanstack/react-query";
import { ChevronDown, Play, UserCheck } from "lucide-mui";
import { useState } from "react";
import toast from "react-hot-toast";
import FormattedDate from "../FormattedDate.tsx";
import { useAgentDataSources } from "../../hooks/resources.ts";
import type { StepExecution } from "../../api/client.ts";
import { userHasGlobalScope } from "../../auth/scopes.ts";
import { useAuth } from "../../contexts/AuthContext.tsx";

// "clay" → "Clay", for when the data-source catalog can't be read.
const keyLabel = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

/** What continuing with this step does and what it may spend, for the person deciding. */
export function describeStep(
  e: StepExecution,
  sourceLabel: (key: string) => string = keyLabel,
): string {
  const config = e.step_config;
  switch (e.step_type) {
    case "agent": {
      const sources = (config.data_sources ?? {}) as Record<string, Record<string, unknown> | null>;
      const parts = ["Runs the agent and bills the organisation's Claude plan"];
      // Every opted-in source that buys data says its cap.
      for (const [key, source] of Object.entries(sources)) {
        const cap = source?.max_spend_per_run;
        if (cap !== undefined && cap !== null) {
          parts.push(`may buy ${sourceLabel(key)} data up to $${cap} per run`);
        }
      }
      return `${parts.join("; ")}.`;
    }
    case "add_to_prospect_list":
      return "Adds the company to the collection.";
    case "slack_message":
      return `Posts a message to ${config.channel_name ? `#${config.channel_name}` : "Slack"}.`;
    default:
      return "";
  }
}

interface Props {
  executions: StepExecution[];
  canDecide: boolean;
  approve: (executionId: string) => Promise<unknown>;
  onDecided: () => void;
}

/**
 * The steps a person can continue: the parked ones first, then the ones
 * declined earlier. With the data-source labels to describe them by.
 */
export function useContinuableSteps(executions: StepExecution[]) {
  const { user } = useAuth();
  const waiting = executions.filter((e) => e.status === "awaiting_approval");
  const rejected = executions.filter((e) => e.status === "rejected");
  const steps = [...waiting, ...rejected];
  // Only for the source labels; without access the keys stand in.
  const { data: catalog } = useAgentDataSources(null, {
    enabled: steps.length > 0 && userHasGlobalScope(user, "signal_rules:read"),
  });
  const sourceLabel = (key: string) => catalog?.find((o) => o.key === key)?.label ?? keyLabel(key);
  return { steps, sourceLabel };
}

/**
 * The workflow steps parked until a person continues them (and the ones
 * declined earlier, which can still be continued). The human-in-the-loop
 * gate: one button, no note.
 */
export default function StepApprovalBanner({ executions, canDecide, approve, onDecided }: Props) {
  const { steps, sourceLabel } = useContinuableSteps(executions);
  if (!steps.length) return null;
  return (
    <Stack spacing={1}>
      {steps.map((e) => (
        <ContinueAlert
          key={e.id}
          execution={e}
          canDecide={canDecide}
          approve={approve}
          onDecided={onDecided}
          sourceLabel={sourceLabel}
        />
      ))}
    </Stack>
  );
}

interface ContinueStepButtonProps {
  execution: StepExecution;
  approve: (executionId: string) => Promise<unknown>;
  onDecided: () => void;
  sourceLabel: (key: string) => string;
  // What the step does, on hover; off where the step is described next to it.
  describe?: boolean;
}

/** Continue one parked (or declined) step; its tooltip says what that does. */
export function ContinueStepButton({
  execution: e,
  approve,
  onDecided,
  sourceLabel,
  describe = true,
}: ContinueStepButtonProps) {
  const run = useMutation({
    mutationFn: () => approve(e.id),
    onSuccess: () => {
      toast.success(`Continued: ${e.step_label}`);
      onDecided();
    },
    // Someone else may have decided meanwhile.
    onError: () => onDecided(),
  });
  const isRejected = e.status === "rejected";
  const button = (
    <Button
      size="small"
      variant={isRejected ? "outlined" : "contained"}
      color={isRejected ? "inherit" : "warning"}
      startIcon={<Play />}
      disabled={run.isPending}
      onClick={() => run.mutate()}
      sx={{ whiteSpace: "nowrap", alignSelf: "center" }}
    >
      {isRejected ? "Continue anyway" : "Continue"}
    </Button>
  );
  if (!describe) return button;
  return (
    <Tooltip
      title={
        <>
          <strong>
            {isRejected ? "Declined" : "Waiting for you"}: {e.step_label}
          </strong>
          <br />
          {describeStep(e, sourceLabel)}
          {e.workflow_name && (
            <>
              <br />
              Workflow: {e.workflow_name}
            </>
          )}
          {isRejected && e.reviewed_at && (
            <>
              <br />
              <FormattedDate value={e.reviewed_at} format="datetime" />
              {e.review_note && ` · ${e.review_note}`}
            </>
          )}
        </>
      }
    >
      <span>{button}</span>
    </Tooltip>
  );
}

/**
 * Several steps to continue: "Continue all" runs every waiting one at once,
 * the caret beside it opens a menu to continue one step at a time.
 */
export function ContinueStepsSplitButton({
  steps,
  approve,
  onDecided,
  sourceLabel,
  onContinueAll,
  continuingAll,
}: Omit<ContinueStepButtonProps, "execution" | "describe"> & {
  steps: StepExecution[];
  // Continues every waiting step (declined ones stay declined).
  onContinueAll: () => void;
  continuingAll: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const run = useMutation({
    mutationFn: (e: StepExecution) => approve(e.id),
    onSuccess: (_, e) => {
      toast.success(`Continued: ${e.step_label}`);
      onDecided();
    },
    // Someone else may have decided meanwhile.
    onError: () => onDecided(),
  });
  const waiting = steps.filter((e) => e.status === "awaiting_approval").length;
  const busy = continuingAll || run.isPending;
  return (
    <>
      <ButtonGroup
        size="small"
        variant={waiting ? "contained" : "outlined"}
        color={waiting ? "warning" : "inherit"}
        disabled={busy}
        sx={{ flexShrink: 0 }}
      >
        <Tooltip title={waiting ? "" : "Every step was declined: continue them one at a time"}>
          <span>
            <Button
              startIcon={<Play />}
              disabled={busy || !waiting}
              onClick={onContinueAll}
              sx={{ whiteSpace: "nowrap", height: "100%" }}
            >
              Continue all ({waiting})
            </Button>
          </span>
        </Tooltip>
        <Button
          aria-label="Continue one step"
          aria-haspopup="menu"
          aria-expanded={anchor ? "true" : undefined}
          onClick={(ev) => setAnchor(ev.currentTarget)}
          sx={{ px: 0.5, minWidth: 0 }}
        >
          <ChevronDown
            fontSize="small"
            style={{ transition: "transform 150ms", transform: anchor ? "rotate(180deg)" : "none" }}
          />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 0.5, maxWidth: 400 } } }}
      >
        {steps.map((e) => (
          <MenuItem
            key={e.id}
            onClick={() => {
              setAnchor(null);
              run.mutate(e);
            }}
            sx={{ whiteSpace: "normal" }}
          >
            <ListItemText
              primary={e.status === "rejected" ? `${e.step_label} (declined)` : e.step_label}
              secondary={describeStep(e, sourceLabel)}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

function ContinueAlert({
  execution: e,
  canDecide,
  approve,
  onDecided,
  sourceLabel,
}: { execution: StepExecution; sourceLabel: (key: string) => string } & Omit<Props, "executions">) {
  const isRejected = e.status === "rejected";
  return (
    <Alert
      severity={isRejected ? "info" : "warning"}
      icon={<UserCheck />}
      sx={{ "& .MuiAlert-message": { flexGrow: 1 } }}
      action={
        canDecide ? (
          <ContinueStepButton
            execution={e}
            approve={approve}
            onDecided={onDecided}
            sourceLabel={sourceLabel}
            describe={false}
          />
        ) : undefined
      }
    >
      <AlertTitle sx={{ mb: 0.5 }}>
        {isRejected ? "Declined" : "Waiting for you to continue"}: {e.step_label}
      </AlertTitle>
      <Typography variant="body2">{describeStep(e, sourceLabel)}</Typography>
      {e.workflow_name && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          Workflow: {e.workflow_name}
        </Typography>
      )}
      {isRejected && e.reviewed_at && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
          <FormattedDate value={e.reviewed_at} format="datetime" />
          {e.review_note && ` · ${e.review_note}`}
        </Typography>
      )}
    </Alert>
  );
}
